/*
  SettingsTab.jsx — Collection Targets + FY Targets + Data Eraser + Reminders +
  Sales Team / Company Profile / Email / Auto-Reminders / FX Rate settings.
  Extracted from CollectionPlanningPage.jsx (Feb 2026 refactor).
*/
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  IndianRupee, Save, Loader2, Edit2, Trash2, Search,
} from "lucide-react";
import SalesTeamSettings from "@/components/collection/SalesTeamSettings";
import CompanyProfileSettings from "@/components/collection/CompanyProfileSettings";
import EmailSettings from "@/components/collection/EmailSettings";
import AutoReminderSettings from "@/components/collection/AutoReminderSettings";
import RemindersSettings from "@/components/collection/RemindersSettings";
import FxRateCard from "@/components/collection/FxRateCard";
import { fmt, sanitizeNumeric } from "@/pages/collection-planning/planningUtils";

export default function SettingsTab({ ctx }) {
  const {
    targets, setTargets, saving, handleSaveTargets,
    fyForm, setFyForm, editingFy, setEditingFy, fyTargets, handleSaveFy, handleEditFy, handleDeleteFy,
    eraseRange, setEraseRange, erasePreview, setErasePreview,
    erasePreviewLoading, handleErasePreview, applyEraserQuickRange, setEraseConfirmOpen,
    erasePreviewRef, fmtDateLong,
    defaultFxRate, loadFxRate,
  } = ctx;

  return (
    <div className="space-y-6">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Collection Targets</h1>
                <p className="text-xs text-slate-500 mt-1">Default targets are used as fallback when no FY-specific target is configured.</p>
              </div>

              {/* Sales Team Management */}
              <SalesTeamSettings />

              {/* Company Profile — used for invoicing */}
              <CompanyProfileSettings />

              {/* Email Configuration (SMTP / AWS SES) */}
              <EmailSettings />

              {/* Invoice Auto-Reminders */}
              <AutoReminderSettings />

              {/* FX Rate config */}
              <FxRateCard defaultFxRate={defaultFxRate} onSaved={loadFxRate} />

              {/* Default Targets */}
              <Card>
                <CardContent className="p-5 sm:p-6 space-y-5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-sm font-semibold text-slate-800">Default Targets (fallback)</h2>
                    <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded font-semibold">Used when no FY target found</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[["daily_target", "Daily"], ["weekly_target", "Weekly"], ["monthly_target", "Monthly"], ["annual_target", "Annual"]].map(([k, l]) => (
                      <div key={k}>
                        <Label className="text-xs font-semibold text-slate-600">{l} Target</Label>
                        <div className="relative mt-1">
                          <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input type="text" inputMode="numeric" autoComplete="off" className="pl-9 h-11 text-base font-medium" value={targets[k] ? String(targets[k]) : ""} onFocus={(e) => e.target.select()} onChange={e => { const clean = sanitizeNumeric(e.target.value); setTargets(p => ({ ...p, [k]: clean === "" ? 0 : parseInt(clean, 10) })); }} placeholder="0" data-testid={`default-${k}`} />
                          <p className="text-[10px] text-slate-400 mt-1">≈ ₹{((targets[k] || 0) / 100000).toFixed(2)} L</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button onClick={handleSaveTargets} disabled={saving} className="bg-[#0066CC] hover:bg-[#0052A3] w-full sm:w-auto sm:px-8 h-11" data-testid="save-default-targets">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save Default Targets</Button>
                </CardContent>
              </Card>

              {/* FY Targets */}
              <Card>
                <CardContent className="p-5 sm:p-6 space-y-5">
                  <div>
                    <h2 className="text-sm font-semibold text-slate-800">Financial Year Targets</h2>
                    <p className="text-[11px] text-slate-500 mt-0.5">Indian FY (Apr–Mar). Used by Reports & Analytics for the matching period.</p>
                  </div>

                  {/* Add / Edit form */}
                  <div className="bg-slate-50 rounded-xl p-5 space-y-4 border border-slate-200">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <p className="text-sm font-semibold text-slate-700">{editingFy ? `Editing: ${editingFy.fy_label}` : "Add Financial Year Target"}</p>
                      {!editingFy && (
                        <p className="text-[11px] text-slate-500">FY <strong>{fyForm.fy_start_year}-{String(fyForm.fy_start_year + 1).slice(-2)}</strong></p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                      <div>
                        <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">FY Start Year</Label>
                        <Input type="text" inputMode="numeric" autoComplete="off" maxLength="4" className="h-11 text-base font-medium mt-1" value={fyForm.fy_start_year ? String(fyForm.fy_start_year) : ""} onFocus={(e) => e.target.select()} onChange={e => { const clean = sanitizeNumeric(e.target.value).slice(0, 4); setFyForm(p => ({ ...p, fy_start_year: clean === "" ? 0 : parseInt(clean, 10) })); }} disabled={!!editingFy} placeholder="2025" data-testid="fy-year-input" />
                      </div>
                      {[["daily_target", "Daily"], ["weekly_target", "Weekly"], ["monthly_target", "Monthly"], ["annual_target", "Annual"]].map(([k, l]) => (
                        <div key={k}>
                          <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{l}</Label>
                          <div className="relative mt-1">
                            <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                            <Input type="text" inputMode="numeric" autoComplete="off" className="pl-8 h-11 text-base font-medium" value={fyForm[k] ? String(fyForm[k]) : ""} onFocus={(e) => e.target.select()} onChange={e => { const clean = sanitizeNumeric(e.target.value); setFyForm(p => ({ ...p, [k]: clean === "" ? 0 : parseInt(clean, 10) })); }} placeholder="0" data-testid={`fy-${k}`} />
                          </div>
                          <p className="text-[10px] text-slate-400 mt-1">≈ ₹{((fyForm[k] || 0) / 100000).toFixed(2)} L</p>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2 justify-end pt-1">
                      {editingFy && <Button variant="ghost" size="sm" className="h-10" onClick={() => { setEditingFy(null); setFyForm({ fy_start_year: new Date().getMonth() >= 3 ? new Date().getFullYear() : new Date().getFullYear() - 1, daily_target: 0, weekly_target: 0, monthly_target: 0, annual_target: 0 }); }}>Cancel</Button>}
                      <Button onClick={handleSaveFy} disabled={saving || !fyForm.fy_start_year} className="bg-[#0066CC] hover:bg-[#0052A3] gap-2 h-10 px-5" data-testid="save-fy-target">
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} {editingFy ? "Update FY Target" : "Add FY Target"}
                      </Button>
                    </div>
                  </div>

                  {/* List of FY Targets */}
                  <div>
                    {fyTargets.length === 0 ? (
                      <p className="text-center text-xs text-slate-400 py-6">No FY-specific targets yet. Defaults above will be used.</p>
                    ) : (
                      <div className="overflow-x-auto rounded-lg border border-slate-200">
                        <table className="w-full text-xs sm:text-sm" data-testid="fy-targets-table">
                          <thead className="bg-slate-100">
                            <tr>
                              <th className="text-left p-3 font-semibold text-slate-600">FY</th>
                              <th className="text-right p-3 font-semibold text-slate-600 hidden sm:table-cell">Daily</th>
                              <th className="text-right p-3 font-semibold text-slate-600 hidden sm:table-cell">Weekly</th>
                              <th className="text-right p-3 font-semibold text-slate-600">Monthly</th>
                              <th className="text-right p-3 font-semibold text-slate-600">Annual</th>
                              <th className="text-right p-3 font-semibold text-slate-600 w-[100px]">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {fyTargets.map(fy => (
                              <tr key={fy.id} className="border-t border-slate-100 hover:bg-slate-50">
                                <td className="p-3 font-semibold text-slate-800">{fy.fy_label}</td>
                                <td className="p-3 text-right text-slate-700 hidden sm:table-cell">{fmt(fy.daily_target)}</td>
                                <td className="p-3 text-right text-slate-700 hidden sm:table-cell">{fmt(fy.weekly_target)}</td>
                                <td className="p-3 text-right text-slate-700">{fmt(fy.monthly_target)}</td>
                                <td className="p-3 text-right font-semibold text-emerald-600">{fmt(fy.annual_target)}</td>
                                <td className="p-3 text-right whitespace-nowrap">
                                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => handleEditFy(fy)}><Edit2 className="w-3.5 h-3.5" /></Button>
                                  <Button variant="ghost" size="sm" className="h-7 text-xs text-red-600" onClick={() => handleDeleteFy(fy.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Data Eraser */}
              <Card className="border-red-200">
                <CardContent className="p-5 sm:p-6 space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </div>
                    <div>
                      <h2 className="text-sm font-semibold text-slate-800">Data Eraser — Receipts</h2>
                      <p className="text-[11px] text-slate-500 mt-0.5">Permanently delete all receipt records within a selected date range. This action cannot be undone.</p>
                    </div>
                  </div>

                  {/* Quick range chips — removes MM/DD vs DD/MM ambiguity */}
                  <div className="flex flex-wrap gap-1.5">
                    <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider self-center mr-1">Quick select:</span>
                    {[
                      { key: "currentMonth", label: "Current Month" },
                      { key: "currentFY", label: "Current FY" },
                      { key: "last90", label: "Last 90 Days" },
                      { key: "allTime", label: "All Time" },
                    ].map(c => (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => applyEraserQuickRange(c.key)}
                        className="text-[11px] px-2.5 py-1 rounded-full border border-slate-200 bg-white hover:bg-slate-50 hover:border-[#0066CC] hover:text-[#0066CC] transition-colors"
                        data-testid={`eraser-quick-${c.key}`}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                    <div>
                      <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Start Date</Label>
                      <Input type="date" className="h-11 mt-1" value={eraseRange.start} onChange={e => { setEraseRange(p => ({ ...p, start: e.target.value })); setErasePreview(null); }} data-testid="eraser-start-date" />
                      <p className="text-[10px] text-slate-500 mt-1">{eraseRange.start ? fmtDateLong(eraseRange.start) : "— pick a date —"}</p>
                    </div>
                    <div>
                      <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">End Date</Label>
                      <Input type="date" className="h-11 mt-1" value={eraseRange.end} onChange={e => { setEraseRange(p => ({ ...p, end: e.target.value })); setErasePreview(null); }} data-testid="eraser-end-date" />
                      <p className="text-[10px] text-slate-500 mt-1">{eraseRange.end ? fmtDateLong(eraseRange.end) : "— pick a date —"}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" className="h-11 flex-1" onClick={handleErasePreview} disabled={erasePreviewLoading || !eraseRange.start || !eraseRange.end} data-testid="eraser-preview-btn">
                        {erasePreviewLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />} Preview
                      </Button>
                    </div>
                  </div>

                  {erasePreview ? (
                    <div
                      ref={erasePreviewRef}
                      className={`rounded-lg border p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${erasePreview.count > 0 ? "border-red-200 bg-red-50/60" : "border-slate-200 bg-slate-50"}`}
                      data-testid="eraser-preview-result"
                    >
                      <div className="text-sm text-slate-800">
                        {erasePreview.count > 0 ? (
                          <>
                            <p>
                              <span className="font-bold text-red-700">{erasePreview.count}</span> receipt{erasePreview.count === 1 ? "" : "s"} totalling <span className="font-bold text-red-700">₹{fmt(erasePreview.total_amount)}</span> between
                              <span className="font-semibold"> {fmtDateLong(erasePreview.start_date)}</span> and
                              <span className="font-semibold"> {fmtDateLong(erasePreview.end_date)}</span>.
                            </p>
                            <p className="text-[11px] text-red-600 mt-1">This will permanently remove these records from the database.</p>
                          </>
                        ) : (
                          <div>
                            <p className="text-slate-600">
                              <span className="font-bold text-slate-800">No receipts</span> found between <span className="font-semibold">{fmtDateLong(erasePreview.start_date)}</span> and <span className="font-semibold">{fmtDateLong(erasePreview.end_date)}</span>.
                            </p>
                            <p className="text-[11px] text-slate-500 mt-1">Tip: double-check the dates above — date inputs may show in your browser&apos;s regional format. The unambiguous dates are shown below each input.</p>
                          </div>
                        )}
                      </div>
                      <Button
                        className="bg-red-600 hover:bg-red-700 text-white h-10 px-5 shrink-0 disabled:bg-slate-300"
                        disabled={erasePreview.count === 0}
                        onClick={() => setEraseConfirmOpen(true)}
                        data-testid="eraser-erase-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> Erase {erasePreview.count > 0 ? `${erasePreview.count} Receipt${erasePreview.count === 1 ? "" : "s"}` : "Data"}
                      </Button>
                    </div>
                  ) : (
                    <p className="text-[11px] text-slate-400 italic">Pick a quick range above or select dates manually, then click <span className="font-medium">Preview</span> — the count of matching receipts will appear here.</p>
                  )}
                </CardContent>
              </Card>

              {/* Reminders (SMTP + WhatsApp + Templates + Rules + Sent Log) */}
              <div>
                <h2 className="text-lg font-bold text-slate-900">Automated Reminders</h2>
                <p className="text-xs text-slate-500 mt-1">Configure your email (SMTP) &amp; WhatsApp channels, define templates and rules — the system will dispatch reminders for overdue billing lines.</p>
              </div>
              <RemindersSettings />
            </div>

  );
}
